import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { DailyVideoCall } from "@/components/calls/DailyVideoCall";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Video, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

type MeetingStatus = "loading" | "not_found" | "pending" | "in_progress" | "completed" | "joined" | "ended";

export default function MeetRoom() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const [status, setStatus] = useState<MeetingStatus>("loading");
  const [meetingInfo, setMeetingInfo] = useState<any>(null);
  const [guestName, setGuestName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!meetingId) {
      setStatus("not_found");
      return;
    }
    fetchMeeting();
  }, [meetingId]);

  const fetchMeeting = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from("v_public_meeting_info")
        .select("*")
        .eq("id", meetingId)
        .maybeSingle();

      if (fetchError || !data) {
        setStatus("not_found");
        return;
      }

      setMeetingInfo(data);

      // Guarda defensiva: reuniões agendadas dentro do horário previsto
      // permanecem acessíveis mesmo se status === 'completed' (cobre legado e
      // race conditions onde o webhook fechou a sala antes do end_at).
      const isScheduledStillActive =
        data.type === "scheduled" &&
        data.scheduled_end_at &&
        new Date(data.scheduled_end_at).getTime() > Date.now();

      if (data.status === "completed" && !isScheduledStillActive) {
        setStatus("completed");
      } else {
        setStatus(data.status === "in_progress" ? "in_progress" : "pending");
      }
    } catch {
      setStatus("not_found");
    }
  };

  const handleJoin = () => {
    if (!guestName.trim()) {
      setError("Por favor, insira seu nome.");
      return;
    }
    setError("");
    setStatus("joined");
  };

  // Loading state
  if (status === "loading") {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not found
  if (status === "not_found") {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <AlertCircle className="w-12 h-12 mx-auto text-destructive" />
            <h2 className="text-xl font-semibold">Reunião não encontrada</h2>
            <p className="text-muted-foreground text-sm">
              O link pode ter expirado ou a reunião não existe.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Meeting ended (user left)
  if (status === "ended") {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h2 className="text-xl font-semibold">Obrigado pela participação!</h2>
            <p className="text-muted-foreground text-sm">
              A reunião foi encerrada. Você pode fechar esta janela.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completed (already ended before user arrived)
  if (status === "completed") {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <CheckCircle2 className="w-12 h-12 mx-auto text-muted-foreground" />
            <h2 className="text-xl font-semibold">Reunião encerrada</h2>
            <p className="text-muted-foreground text-sm">
              Esta reunião já foi concluída.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // In the call
  if (status === "joined" && meetingInfo?.daily_room_url && meetingInfo?.client_token) {
    return (
      <div className="h-[100dvh] w-screen bg-slate-900 p-2">
        <DailyVideoCall
          roomUrl={meetingInfo.daily_room_url}
          token={meetingInfo.client_token}
          onMeetingEnded={() => setStatus("ended")}
        />
      </div>
    );
  }

  // Waiting / Join form
  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <Video className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-xl">Reunião por Vídeo</CardTitle>
          {meetingInfo?.host_name && (
            <p className="text-sm text-muted-foreground">
              Consultor: <span className="font-medium text-foreground">{meetingInfo.host_name}</span>
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "pending" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <p className="text-sm text-amber-700">
                Aguardando o consultor iniciar a reunião...
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Seu nome</label>
            <Input
              placeholder="Digite seu nome"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleJoin()}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          <Button
            className="w-full gap-2"
            onClick={handleJoin}
            disabled={!meetingInfo?.client_token}
          >
            <Video className="w-4 h-4" />
            Entrar na Reunião
          </Button>

          {!meetingInfo?.client_token && (
            <p className="text-xs text-center text-muted-foreground">
              Token de acesso não disponível. Contacte o consultor.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
